import * as Sentry from "@sentry/react";

import { isBlast, isTest } from "./miscUtils";
import { getSentryDsn } from "./runtimeConfig";

// DSN comes from runtime config (env.js). It falls back to the known project DSN
// so that a missing config value can never silently disable error reporting in
// production. Stays empty (disabled) in dev, matching the previous behaviour.
const SENTRY_DSN = import.meta.env.PROD
	? getSentryDsn() ||
	  "https://4d1a385918be33d4d26f55daab64d94e@o4509268692369408.ingest.us.sentry.io/4509268693614592"
	: "";

// Initialize with base config
export const baseSentryConfig = {
	dsn: SENTRY_DSN,
	environment: import.meta.env.PROD
		? (isBlast ? "blast" : "portal") + (isTest ? "-test" : "-prod")
		: "localhost" + (isBlast ? "-blast" : "-portal"),
	_experiments: {
		enableLogs: true,
	},
	integrations: [
		// send console.log, console.error, and console.warn calls as logs to Sentry
		Sentry.consoleLoggingIntegration({ levels: ["error"] }),
	],
	// Setting this option to true will send default PII data to Sentry.
	// For example, automatic IP address collection on events
	tracesSampleRate: 0.0, // disables performance monitoring
	// Drop known-noise errors that aren't actionable and just bury real issues.
	ignoreErrors: [
		// Benign layout-notification warnings the browser raises; not real errors.
		"ResizeObserver loop limit exceeded",
		"ResizeObserver loop completed with undelivered notifications",
		// Recoverable OIDC callback race — the sign-in state was already
		// consumed or lost. Auth self-heals by restarting the flow (auth.tsx),
		// so this isn't actionable noise.
		"No matching state found in storage",
	],
	// Ignore errors originating from browser extensions / injected third-party
	// scripts — they're not our code and aren't actionable.
	denyUrls: [
		/^chrome:\/\//i,
		/^chrome-extension:\/\//i,
		/^moz-extension:\/\//i,
		/^safari-extension:\/\//i,
		/^safari-web-extension:\/\//i,
	],
	// Append the clicked element's text to ui.click breadcrumbs (Sentry records
	// only the CSS selector by default) so the trail is human-readable.
	beforeBreadcrumb(breadcrumb, hint) {
		if (breadcrumb.category === "ui.click") {
			const text = (hint?.event as any)?.target?.innerText
				?.trim()
				.slice(0, 80);
			if (text) breadcrumb.message += ` "${text}"`;
		}
		return breadcrumb;
	},
};

const GUID_REGEX =
	/^(?:[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/i;

// Any GUID-shaped path segment. Looser than GUID_REGEX above (a strict RFC-4122
// validator) on purpose — here we want to catch *any* id so it can't fragment
// Sentry grouping, including non-RFC ids the API might use.
const GUID_SEGMENT =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Normalise a URL path for Sentry grouping: drop the query string and replace
// GUID and numeric path segments with `:id`, so errors group by route template
// (e.g. `/organisations/:id/users/:id`) instead of shattering into one issue
// per id.
export const normaliseEndpoint = (endpoint: string): string =>
	(endpoint ?? "")
		.split("?")[0]
		.split("/")
		.map((segment) =>
			GUID_SEGMENT.test(segment) || /^\d+$/.test(segment)
				? ":id"
				: segment,
		)
		.join("/");

export const cleanObject = (obj: any, seen = new WeakSet()): any => {
	// Handle circular references
	if (obj && typeof obj === "object") {
		if (seen.has(obj)) {
			return null;
		}
		seen.add(obj);
	}

	if (!obj || typeof obj !== "object") {
		// If it's a string, check if it's a GUID
		if (typeof obj === "string" && GUID_REGEX.test(obj)) {
			return obj;
		}
		return null;
	}

	if (Array.isArray(obj)) {
		// Always return an array, even if empty
		const cleaned = obj
			.map((item) => {
				return cleanObject(item, seen);
			})
			.filter((item) => item !== null);
		return cleaned;
	}

	const cleaned: any = {};
	for (const [key, value] of Object.entries(obj)) {
		const cleanedValue = cleanObject(value, seen);
		if (cleanedValue !== null) {
			cleaned[key] = cleanedValue;
		}
	}
	return Object.keys(cleaned).length > 0 ? cleaned : null;
};

// Function to update Sentry with the appropriate configuration
export const updateSentry = (isMinimal = true) => {
	// const isMinimal =
	// 	blockedOrgsFromBlast.map((org) => org.id).includes(orgId) || true;

	Sentry.init({
		...baseSentryConfig,
		sendDefaultPii: true, // enables automatic sending of IP, email, etc.=
		beforeSend: isMinimal
			? (event) => {
					// Only keep the error information and GUIDs
					return {
						...event,
						exception: event.exception,
						// userId:
						// Keep only the UI-click and navigation trail (what the
						// user did leading up to the error). Network/console/xhr
						// breadcrumbs are dropped as they carry the most PII.
						breadcrumbs: event.breadcrumbs?.filter(
							(b) =>
								b.category === "ui.click" ||
								b.category === "navigation",
						),
						// user: undefined,
						request: undefined,
						extra: undefined,
						contexts: event.contexts,
						sdk: undefined,
						transaction: undefined,
						spans: undefined,
						measurements: undefined,
						debug_meta: undefined,
						// fingerprint: undefined,
						level: undefined,
						// message: undefined,
						tags: {
							...event.tags,
							page_url: window.location.pathname,
							page_href: window.location.href,
						},
					};
			  }
			: undefined,
		initialScope: {
			tags: {
				tracking_level: isMinimal ? "minimal" : "full",
			},
		},
	});
};
